import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useParams } from 'js/lib/useRouter';

import EnterpriseLearningPathContext from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import EnterpriseOrgScopedLearningPathPageQuery from 'bundles/enterprise-learning-path-description-page/queries/EnterpriseOrgScopedLearningPathPageQuery.graphql';
import EnterpriseOrgScopedLearningPathProgramQuery from 'bundles/enterprise-learning-path-description-page/queries/EnterpriseOrgScopedLearningPathProgramQuery.graphql';
import type {
  EnterpriseOrgScopedLearningPathPageQuery as EnterpriseOrgScopedLearningPathPageQueryType,
  EnterpriseOrgScopedLearningPathPageQueryVariables,
} from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseOrgScopedLearningPathPageQuery';
import type {
  EnterpriseOrgScopedLearningPathProgramQuery as EnterpriseOrgScopedLearningPathProgramQueryType,
  EnterpriseOrgScopedLearningPathProgramQueryVariables,
} from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseOrgScopedLearningPathProgramQuery';
import type { EnterpriseProgramFromSlugFragment } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import useGetProgramCurriculumQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/useGetProgramCurriculumQuery';
import EnterpriseProgramContext from 'bundles/unified-common/contexts/EnterpriseProgramContext';

type PropsToComponent = {
  children: React.ReactNode;
};

const EnterpriseOrgScopedLearningPathPageDataProvider = ({ children }: PropsToComponent) => {
  const { orgSlug, badgeTemplateSlug } = useParams();

  const {
    data: badgeData,
    loading: isBadgeLoading,
    error,
  } = useQuery<EnterpriseOrgScopedLearningPathPageQueryType, EnterpriseOrgScopedLearningPathPageQueryVariables>(
    EnterpriseOrgScopedLearningPathPageQuery,
    {
      skip: !orgSlug || !badgeTemplateSlug,
      variables: { orgSlug: orgSlug ?? '', badgeTemplateSlug: badgeTemplateSlug ?? '' },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    }
  );

  const enterpriseLearningPathPageData = badgeData?.EnterpriseBadge?.findByOrgSlugAndBadgeTemplateSlug;

  // A badge template is associated with exactly one program within an org, so we use the first id.
  const programId = enterpriseLearningPathPageData?.programIds?.[0] ?? '';

  const { data: programData, loading: isProgramLoading } = useQuery<
    EnterpriseOrgScopedLearningPathProgramQueryType,
    EnterpriseOrgScopedLearningPathProgramQueryVariables
  >(EnterpriseOrgScopedLearningPathProgramQuery, {
    skip: !programId,
    variables: { programId },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const program = programData?.EnterpriseProgram?.queryByIds?.elements?.[0] as
    EnterpriseProgramFromSlugFragment | undefined;

  const curriculumId = program?.naptimeCurriculum?.id ?? undefined;
  const { curriculum } = useGetProgramCurriculumQuery({ curriculumId });

  const matchingBadge = (_: { badgeTemplateId?: string }) =>
    _.badgeTemplateId === enterpriseLearningPathPageData?.enterpriseBadge?.id;
  const learningPath =
    curriculum?.definition?.coreTracks?.find(matchingBadge) ??
    curriculum?.definition?.electiveTracks?.find(matchingBadge);

  return (
    <EnterpriseProgramContext.Provider value={{ program, isProgramLoading }}>
      <EnterpriseLearningPathContext.Provider
        value={{
          learningPathPage: enterpriseLearningPathPageData,
          learningPathName: learningPath?.title,
          isLPDataLoading: isBadgeLoading || (!!programId && isProgramLoading),
          collectionId: learningPath?.id,
          error,
        }}
      >
        {children}
      </EnterpriseLearningPathContext.Provider>
    </EnterpriseProgramContext.Provider>
  );
};

export default EnterpriseOrgScopedLearningPathPageDataProvider;
